import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import paper from 'paper/dist/paper-core';
import random from 'lodash/random';
import AppContext from '../../context/AppContext';
import Track from './Track';
import Car from './Car';
import Lights from './Lights/Lights';
import RaceInfo from './RaceInfo/RaceInfo';

import lane1 from '../../assets/lane-1.svg';
import lane2 from '../../assets/lane-2.svg';

import styles from './Game.module.scss';

const COUNTDOWN_TIME = 5;
let cars = [];
let tracks = [];
let startInterval = null;

function getTimeDiff(driver1, driver2) {
    const { time: time1, progress: progress1 } = driver1;
    const { time: time2, progress: progress2 } = driver2;
    const diff = ((time1 / progress1) - (time2 / progress2)) * progress1;
    return diff;
}

function getDriverStatuses() {
    const status = [];
    
    cars.sort((a,b) => {
        const { time: time1, progress: progress1 } = a.getDriverStatus();
        const { time: time2, progress: progress2 } = b.getDriverStatus();
        const currentTime1 = (time1 / progress1);
        const currentTime2 = (time2 / progress2);

        if (time1 === null) {
            return 1;
        }
    
        if (time2 === null) {
            return -1;
        }

        if (currentTime1 === currentTime2) {
            return 0;
        }
        
        return currentTime1 < currentTime2 ? -1 : 1;
    });

    for (let index = 0; index < cars.length; index++) {
        const car = cars[index];
        const prevCar = cars[index - 1];
        const driver1 = car.getDriverStatus();
        let diff = null;
        
        if( prevCar ) {
            const driver2 = prevCar.getDriverStatus();
            diff = getTimeDiff( driver1, driver2 );
        }

        status.push({
            ...driver1,
            interval: diff,
            type: car.data.type
        });
    }

    return status;
}

function setScale(){
    const actual_width = 1336;
    const actual_height = 816;

    paper.view.viewSize = new paper.Size(window.innerWidth * 2, window.innerHeight * 2);

    if( window.innerWidth > 992 ) {
        paper.view.rotation = 0;
    } else {  
        paper.view.rotation = -90;
    }        

    const bounds = paper.view.bounds;
    const scale = Math.min(
        bounds.height / actual_height,
        bounds.width / actual_width
    ); 
    
    paper.view.scale( scale );
    paper.view.setCenter(0,0);
}

function Game({ laps = 5, speed = 25, ...other }) {
    const { state: {
        isPlaying, 
        isRacing, 
        isRaceComplete, 
        drivers,
        driverStatus, 
        isReset
    }, dispatch } = useContext( AppContext );
    const [isInit, setInit] = useState(false);
    const [coundownTimer, setCoundownTimer] = useState( COUNTDOWN_TIME );
    const ref = useRef( null );
    const isWinner = drivers.player?.isWinner;

    const startCountdown = useCallback(() => {
        startInterval = setInterval(() => {
            setCoundownTimer(coundownTimer => coundownTimer - 1)
        }, 1000);
    }, []);

    const restartRace = useCallback(() => {
        cars.forEach( car => car.reset() );

        setCoundownTimer( COUNTDOWN_TIME );
        clearInterval( startInterval );
        paper.view.onFrame = null;

        startCountdown();
    },[startCountdown]);

    const handleNewSector = useCallback( car => {
        const status = getDriverStatuses();

        // Only update diff on lead car
        if( car.data.type === status[0].type ) {
            dispatch({ type: 'SET_DRIVER_STATUS', payload: status });
        }
    }, [ dispatch ]);

    const handleCarFinished = useCallback( car  => {
        let carsFinished = 0;
        
        cars.forEach( car => {
            const carStatus = car.getDriverStatus();

            if ( carStatus.isFinished || carStatus.hasStopped ) {
                carsFinished++;
            }
        });

        if( carsFinished === cars.length ) {
            dispatch({ type: 'RACE_FINISHED', payload: getDriverStatuses() });
        }
    }, [ dispatch ]);

    const addCar = useCallback( ({ type, driver, track, speed, shouldCrash }) => {
        const oldCar = cars.find(car => car.data.type === type);
            
        if( oldCar ) {
            oldCar.remove();
            oldCar.off('newsector', handleNewSector);
            oldCar.off('finished', handleCarFinished);
            cars = cars.filter( car => car.data.type !== type);
        }
        
        const car = new Car({
            ...driver,
            sprite: { ...driver.sprite, width: 69, height: 36 },
            path: track.getPath(),
            laps,
            speed,
            shouldCrash
        })
        
        car.data = {
            type,
            ...driver
        };
        
        car.on('newsector', handleNewSector);
        car.on('finished', handleCarFinished);
        cars.push(car);
    },[ laps, handleNewSector, handleCarFinished ]);

    //
    // Initialize
    //
    useEffect(() => {
        async function initialize() {
            paper.setup(ref.current);

            const ctx = ref.current.getContext('2d');
            ctx.imageSmoothingEnabled = false;

            setScale();
            
            const track1 = await new Track(lane1);
            const track2 = await new Track(lane2);

            tracks.push(track1, track2);

            window.addEventListener('resize', setScale);
            window.addEventListener('orientationchange', setScale);

            setInit( true );
        }

        initialize();

        return () => {
            window.removeEventListener('resize', setScale);
            window.removeEventListener('orientationchange', setScale);
        }
    }, [ setInit ]);

    //
    // Add CPU car
    //
    useEffect(() => {
        if( !isInit || !drivers.cpu ) return;

        addCar({
            type: 'CPU', 
            driver: drivers.cpu, 
            track: tracks[0],
            speed,
            shouldCrash: false
        });
    }, [isInit, drivers.cpu, isWinner, laps, speed, addCar]);
    
    //
    // Add player car
    //
    useEffect(() => {
        if( !isInit ) return;

        let carSpeed;
        const shouldCrash = !isWinner && Math.random() < 0.66;

        if( isWinner ) {
            carSpeed = random(speed + 0.5, speed + 1.5);
        } else if( shouldCrash ) {
            carSpeed = random(speed - 0.5, speed + 1.5);
        } else {
            carSpeed = random(speed - 0.5, speed - 1.5);
        }

        addCar({
            type: 'PLAYER',
            driver: drivers.player,
            track: tracks[1],
            speed: carSpeed,
            shouldCrash
        });
    }, [isInit, drivers.player, isWinner, laps, speed, addCar]);

    //
    // Start countdown
    //
    useEffect(() => {
        if( !isPlaying ) return;
        startCountdown();
    },[ isInit, isPlaying, startCountdown ]);

    //
    // Start race
    //
    useEffect(() => {
        if( coundownTimer > 0 ) return;

        clearInterval(startInterval);

        paper.view.onFrame = event => {
            cars.forEach( car => car.render( event ) );
        };

        cars.forEach( car => car.accelerate() );
        
        dispatch({ type: 'RACE_STARTED' });

        return () => {
            paper.view.onFrame = null;
        }
    }, [dispatch, coundownTimer]);

    //
    // Restart race
    //
    useEffect(() => {
        if( !isReset ) return;
        restartRace();
    },[ isReset, restartRace ]);

    //
    // Clean up on unload
    //
    useEffect(() => {
        return () => {
            clearInterval( startInterval );
            cars = [];
        }
    }, []);

    return (
        <div className={[styles.container, isPlaying ? styles.default : styles.rotate].join(' ')} {...other}> 
            <Lights show={isPlaying && !isRacing && !isRaceComplete} count={coundownTimer}/>
            <RaceInfo show={isRacing} lap={driverStatus[0]?.lap} laps={laps} drivers={driverStatus}/>

            <div className={styles.track} style={{ willChange: !isRacing ? 'transform' : 'auto' }}>
                <canvas className={styles.canvas} ref={ref} data-paper-hidpi="off"/>
            </div>

            <div className={styles.background} style={{ willChange: !isRacing ? 'transform' : 'auto' }}/>
        </div>
    );
}

export default Game;